import React, { useState } from "react"
import PropTypes from "prop-types"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Dropdown, DropdownToggle, DropdownMenu } from "reactstrap"

import HeaderIconButton from "./HeaderIconButton"
import { ICONS } from "../../constants/icons"
import useNotificationInbox from "../../hooks/useNotificationInbox"
import {
  markAllNotificationsRead,
  markNotificationsReadByIds,
  openNotification,
} from "../../helpers/notifications"
import {
  clearUnreadInbox,
  markNotificationRead,
} from "../../store/slices/notificationsSlice"

/**
 * NotificationDropdown
 * ------------------------------------------------------------------
 * Header bell dropdown matching Figma (node 40005726:57358): title,
 * "Mark all as read", scrollable rows (dot + message + time), and
 * "View all" linking to /notifications.
 *
 * Badge count and latest 10 unread come from the shared inbox so they
 * stay in sync without a full page reload.
 */
const NotificationDropdown = ({
  onMarkAllRead,
  onNotificationClick,
  onViewAll,
  toggleVariant = "member",
  iconSrc = ICONS.notification,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const { items, unreadCount, sync } = useNotificationInbox()

  const badgeLabel = unreadCount > 99 ? "99+" : String(unreadCount)
  const isAdminToggle = toggleVariant === "admin"

  const handleToggle = () => {
    setIsOpen(prev => {
      const next = !prev
      if (next) sync()
      return next
    })
  }

  const handleMarkAllRead = async () => {
    if (unreadCount === 0) return
    const response = await markAllNotificationsRead()
    if (!response?.data?.success) return
    dispatch(clearUnreadInbox())
    sync()
    onMarkAllRead?.()
  }

  const handleViewAll = () => {
    setIsOpen(false)
    if (onViewAll) {
      onViewAll()
      return
    }
    navigate("/notifications")
  }

  const handleItemClick = async notification => {
    setIsOpen(false)
    onNotificationClick?.(notification)
    openNotification(notification, navigate)

    if (notification.read) return

    const response = await markNotificationsReadByIds([notification.id])
    if (response?.data?.success) {
      dispatch(markNotificationRead(notification.id))
      sync()
    }
  }

  return (
    <Dropdown
      isOpen={isOpen}
      toggle={handleToggle}
      className="notification-dropdown"
    >
      <DropdownToggle tag="div" className="notification-dropdown__toggle">
        {isAdminToggle ? (
          <button
            type="button"
            className={`admin-header__icon-btn${
              isOpen ? " admin-header__icon-btn--active" : ""
            }`}
            aria-label={
              unreadCount > 0
                ? `Notifications, ${badgeLabel} unread`
                : "Notifications"
            }
          >
            <img src={iconSrc} alt="" />
            {unreadCount > 0 ? (
              <span className="admin-header__icon-btn-badge" aria-hidden="true">
                {badgeLabel}
              </span>
            ) : null}
          </button>
        ) : (
          <HeaderIconButton
            iconSrc={iconSrc}
            label="Notifications"
            badgeCount={unreadCount}
            active={isOpen}
          />
        )}
      </DropdownToggle>

      <DropdownMenu end className="notification-dropdown__menu">
        <div className="notification-dropdown__header">
          <span className="notification-dropdown__title">Notifications</span>
          <button
            type="button"
            className="notification-dropdown__mark-all"
            onClick={handleMarkAllRead}
            disabled={unreadCount === 0}
          >
            <span>Mark all as read</span>
            <img src={ICONS.tickDouble} alt="" width={16} height={16} />
          </button>
        </div>

        <ul className="notification-dropdown__list">
          {items.length === 0 ? (
            <li className="notification-dropdown__empty">
              You&rsquo;re all caught up.
            </li>
          ) : (
            items.map(notification => (
              <li key={notification.id}>
                <button
                  type="button"
                  className={`notification-item${
                    !notification.read ? " notification-item--unread" : ""
                  }`}
                  onClick={() => handleItemClick(notification)}
                >
                  <span className="notification-item__dot" aria-hidden="true" />
                  <span className="notification-item__body">
                    <span className="notification-item__title">
                      {notification.title}
                    </span>
                    <span className="notification-item__time">
                      {notification.time}
                    </span>
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>

        <button
          type="button"
          className="notification-dropdown__footer"
          onClick={handleViewAll}
        >
          View all
        </button>
      </DropdownMenu>
    </Dropdown>
  )
}

NotificationDropdown.propTypes = {
  onMarkAllRead: PropTypes.func,
  onNotificationClick: PropTypes.func,
  onViewAll: PropTypes.func,
  toggleVariant: PropTypes.oneOf(["member", "admin"]),
  iconSrc: PropTypes.string,
}

export default NotificationDropdown
